import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    IconButton,
    Tooltip,
    Alert,
    CircularProgress,
    Grid,
    Pagination,
    Stack,
    Divider,
    useMediaQuery,
    useTheme as useMuiTheme
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    Clear as ClearIcon,
    CheckCircle as ActiveIcon,
    Cancel as InactiveIcon
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { 
    fetchBranches, 
    deleteBranch, 
    toggleBranchStatus, 
    setFilters, 
    clearFilters,
    fetchBranchCities 
} from '../../store/slices/branchSlice';
import { Branch } from '../../services/branchApi';
import { useTheme } from '../../contexts/ThemeContext';
import BranchForm from './BranchForm';
import BranchView from './BranchView';
import ConfirmationDialog from '../../components/ConfirmationDialog';
import Toast from '../../components/Toast';
import useToast from '../../hooks/useToast';

const Branches: React.FC = () => {
    const { theme } = useTheme();
    const muiTheme = useMuiTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const isExtraSmall = useMediaQuery(muiTheme.breakpoints.down('xs'));
    const dispatch = useAppDispatch();
    const { toast, showSuccess, showError, hideToast } = useToast();

    const { branches, pagination, filters, loading, error, allCities } = useAppSelector((state) => state.branches);

    // Local state
    const [searchQuery, setSearchQuery] = useState(filters.query);
    const [selectedStatus, setSelectedStatus] = useState(filters.status);
    const [selectedCity, setSelectedCity] = useState(filters.city_id);
    const [currentPage, setCurrentPage] = useState(1);
    const [openForm, setOpenForm] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
    const [viewBranchId, setViewBranchId] = useState<number | null>(null);
    const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [branchToDelete, setBranchToDelete] = useState<Branch | null>(null);

    const loadBranches = useCallback(() => {
        const params = {
            page: currentPage,
            per_page: 20,
            ...filters
        };
        dispatch(fetchBranches(params));
    }, [currentPage, filters, dispatch]);

    // Load branches and cities on component mount
    useEffect(() => {
        loadBranches();
        dispatch(fetchBranchCities());
    }, [loadBranches, dispatch]);

    // Handle search
    const handleSearch = () => {
        dispatch(setFilters({ query: searchQuery }));
        setCurrentPage(1);
    };

    // Handle filter changes
    const handleStatusChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        setSelectedStatus(value);
        dispatch(setFilters({ status: value }));
        setCurrentPage(1);
    };

    const handleCityChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        setSelectedCity(value);
        dispatch(setFilters({ city_id: value }));
        setCurrentPage(1);
    };

    // Clear filters
    const handleClearFilters = () => {
        setSearchQuery('');
        setSelectedStatus('');
        setSelectedCity('');
        dispatch(clearFilters());
        setCurrentPage(1);
    };

    // Handle pagination
    const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
        setCurrentPage(page);
    };

    // Handle branch actions
    const handleViewBranch = (branch: Branch) => {
        setViewBranchId(branch.id);
        setOpenView(true);
    };

    const handleEditBranch = (branch: Branch) => {
        setSelectedBranch(branch);
        setFormMode('edit');
        setOpenForm(true);
    };

    const handleAddBranch = () => {
        setSelectedBranch(null);
        setFormMode('create');
        setOpenForm(true);
    };

    const handleDeleteBranch = (branch: Branch) => {
        setBranchToDelete(branch);
        setDeleteDialog(true);
    };

    const confirmDelete = async () => {
        if (branchToDelete) {
            try {
                await dispatch(deleteBranch(branchToDelete.id)).unwrap();
                showSuccess(`Branch "${branchToDelete.name}" has been deleted successfully`);
                loadBranches();
            } catch (err: any) {
                showError(err?.message || 'Failed to delete branch');
            } finally {
                setDeleteDialog(false);
                setBranchToDelete(null);
            }
        }
    };

    const handleToggleStatus = async (branch: Branch) => {
        try {
            const result = await dispatch(toggleBranchStatus(branch.id)).unwrap();
            const newStatus = result.status === 1 ? 'activated' : 'deactivated';
            showSuccess(`Branch "${branch.name}" has been ${newStatus} successfully`);
            loadBranches();
        } catch (err: any) {
            showError(err?.message || 'Failed to update branch status');
        }
    };

    // Format date
    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    // Get status chip
    const getStatusChip = (status: number) => {
        return status === 1 ? (
            <Chip
                icon={<ActiveIcon />}
                label="Active"
                color="success"
                size="small"
                variant="outlined"
            />
        ) : (
            <Chip
                icon={<InactiveIcon />}
                label="Inactive"
                color="error"
                size="small"
                variant="outlined"
            />
        );
    };

    return (
        <Box sx={{ p: isExtraSmall ? 0.5 : isMobile ? 1 : 3 }}>
            {/* Header */}
            <Box sx={{ 
                mb: 3, 
                display: 'flex', 
                flexDirection: isMobile ? 'column' : 'row',
                justifyContent: 'space-between', 
                alignItems: isMobile ? 'stretch' : 'center',
                gap: isMobile ? 2 : 0
            }}>
                <Typography 
                    variant={isMobile ? "h5" : "h4"} 
                    sx={{ 
                        color: theme.palette.text.primary, 
                        fontWeight: 600,
                        textAlign: isMobile ? 'center' : 'left'
                    }}
                >
                    Branch Management
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddBranch}
                    sx={{
                        bgcolor: theme.palette.secondary.main,
                        borderRadius: 3,
                        '&:hover': {
                            bgcolor: theme.palette.secondary.dark,
                        }
                    }}
                >
                    {isMobile ? 'Add Branch' : 'Add New Branch'}
                </Button>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Filters Card */}
            <Card sx={{ mb: isMobile ? 2 : 3, bgcolor: theme.palette.background.paper }}>
                <CardContent sx={{ p: isExtraSmall ? 1 : isMobile ? 2 : 3 }}>
                    <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
                        <FilterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Filters
                    </Typography>
                    <Grid container spacing={isMobile ? 2 : 2} alignItems="center">
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <TextField
                                fullWidth
                                label="Search by name or code"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                InputProps={{
                                    endAdornment: (
                                        <IconButton onClick={handleSearch}>
                                            <SearchIcon />
                                        </IconButton>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        '& fieldset': {
                                            borderWidth: '1px',
                                        },
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={selectedStatus}
                                    onChange={handleStatusChange}
                                    label="Status"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 3,
                                            '& fieldset': {
                                                borderWidth: '1px',
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem value="">All Status</MenuItem>
                                    <MenuItem value="1">Active</MenuItem>
                                    <MenuItem value="0">Inactive</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <FormControl fullWidth>
                                <InputLabel>City</InputLabel>
                                <Select
                                    value={selectedCity}
                                    onChange={handleCityChange}
                                    label="City"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 3,
                                            '& fieldset': {
                                                borderWidth: '1px',
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem value="">All Cities</MenuItem>
                                    {allCities.map((city) => (
                                        <MenuItem key={city.id} value={city.id}>
                                            {city.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<ClearIcon />}
                                onClick={handleClearFilters}
                                fullWidth
                                sx={{ borderRadius: 3 }}
                            >
                                Clear
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Branches Table */}
            <Card sx={{ bgcolor: theme.palette.background.paper, display: isMobile ? 'none' : 'block' }}>
                <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                    <Box sx={{ position: 'relative', minHeight: 400 }}>
                        {loading && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                                    zIndex: 1,
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        )}

                        <TableContainer component={Paper} sx={{ bgcolor: 'transparent' }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Branch Name</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Code</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>City</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {branches.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                                                <Typography variant="body1" color="textSecondary">
                                                    No branches found
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        branches.map((branch) => (
                                            <TableRow key={branch.id} hover>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {branch.name}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={branch.code}
                                                        color="primary"
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {branch.city?.name || '-'}
                                                </TableCell>
                                                <TableCell>
                                                    {getStatusChip(branch.status)}
                                                </TableCell>
                                                <TableCell>
                                                    {formatDate(branch.created_at)}
                                                </TableCell>
                                                <TableCell>
                                                    <Stack direction="row" spacing={1}>
                                                        <Tooltip title="View Details">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleViewBranch(branch)}
                                                                sx={{ color: theme.palette.primary.main }}
                                                            >
                                                                <ViewIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Edit Branch">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleEditBranch(branch)}
                                                                sx={{ color: theme.palette.secondary.main }}
                                                            >
                                                                <EditIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title={branch.status ? 'Deactivate' : 'Activate'}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleToggleStatus(branch)}
                                                                sx={{ 
                                                                    color: branch.status 
                                                                        ? theme.palette.error.main 
                                                                        : theme.palette.success.main 
                                                                }}
                                                            >
                                                                {branch.status ? <InactiveIcon /> : <ActiveIcon />}
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Delete Branch">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleDeleteBranch(branch)}
                                                                sx={{ color: theme.palette.error.main }}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    {/* Pagination */}
                    {pagination && pagination.last_page > 1 && (
                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                            <Pagination
                                count={pagination.last_page}
                                page={currentPage}
                                onChange={handlePageChange}
                                color="primary"
                                showFirstButton={!isMobile}
                                showLastButton={!isMobile}
                                size={isMobile ? "small" : "medium"}
                                siblingCount={isMobile ? 0 : 1}
                            />
                        </Box>
                    )}

                    {/* Results Summary */}
                    {pagination && (
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Typography variant="body2" color="textSecondary">
                                Showing {pagination.from} to {pagination.to} of {pagination.total} branches
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* Mobile Card Layout */}
            {isMobile && (
                <Card sx={{ bgcolor: theme.palette.background.paper, mt: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ position: 'relative', minHeight: 200 }}>
                            {loading && (
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: 'rgba(255, 255, 255, 0.8)',
                                        zIndex: 1,
                                        borderRadius: 3
                                    }}
                                >
                                    <CircularProgress />
                                </Box>
                            )}
                            {branches.length === 0 ? (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="body1" color="textSecondary">
                                        No branches found
                                    </Typography>
                                </Box>
                            ) : (
                                <Stack spacing={isMobile ? 1 : 2}>
                                    {branches.map((branch) => (
                                        <Card 
                                            key={branch.id} 
                                            sx={{ 
                                                bgcolor: theme.palette.background.paper,
                                                border: `1px solid ${theme.palette.divider}`,
                                                borderRadius: 3
                                            }}
                                        >
                                            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: isMobile ? 1 : 2 }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ fontWeight: 600, mb: isMobile ? 0.5 : 1 }}>
                                                            {branch.name}
                                                        </Typography>
                                                        <Box sx={{ display: 'flex', gap: isMobile ? 0.5 : 1, mb: isMobile ? 0.5 : 1, flexWrap: 'wrap' }}>
                                                            <Chip
                                                                label={branch.code}
                                                                color="primary"
                                                                size="small"
                                                                variant="outlined"
                                                            />
                                                            {getStatusChip(branch.status)}
                                                        </Box>
                                                        <Typography variant="body2" color="textSecondary" sx={{ mb: isMobile ? 0.5 : 1 }}>
                                                            {branch.city?.name || 'No City'}
                                                        </Typography>
                                                    </Box>
                                                    <Stack direction="row" spacing={isMobile ? 0.5 : 1}>
                                                        <IconButton
                                                            size={isMobile ? "small" : "small"}
                                                            onClick={() => handleViewBranch(branch)}
                                                            sx={{ 
                                                                color: theme.palette.primary.main,
                                                                bgcolor: theme.palette.action.hover,
                                                                '&:hover': { bgcolor: theme.palette.action.selected }
                                                            }}
                                                        >
                                                            <ViewIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleEditBranch(branch)}
                                                            sx={{ 
                                                                color: theme.palette.secondary.main,
                                                                bgcolor: theme.palette.action.hover,
                                                                '&:hover': { bgcolor: theme.palette.action.selected }
                                                            }}
                                                        >
                                                            <EditIcon />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleToggleStatus(branch)}
                                                            sx={{ 
                                                                color: branch.status 
                                                                    ? theme.palette.error.main 
                                                                    : theme.palette.success.main,
                                                                bgcolor: theme.palette.action.hover,
                                                                '&:hover': { bgcolor: theme.palette.action.selected }
                                                            }}
                                                        >
                                                            {branch.status ? <InactiveIcon /> : <ActiveIcon />}
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDeleteBranch(branch)}
                                                            sx={{ 
                                                                color: theme.palette.error.main,
                                                                bgcolor: theme.palette.action.hover,
                                                                '&:hover': { bgcolor: theme.palette.action.selected }
                                                            }}
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Stack>
                                                </Box>
                                                <Divider sx={{ my: isMobile ? 1 : 2 }} />
                                                <Grid container spacing={isMobile ? 1 : 2}>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="caption" color="textSecondary" display="block">
                                                            Created
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {formatDate(branch.created_at)}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid size={{ xs: 6 }}>
                                                        <Typography variant="caption" color="textSecondary" display="block">
                                                            Updated
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {formatDate(branch.updated_at)}
                                                        </Typography>
                                                    </Grid>
                                                </Grid>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Mobile Pagination */}
            {isMobile && pagination && pagination.last_page > 1 && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <Pagination
                        count={pagination.last_page}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        showFirstButton={false}
                        showLastButton={false}
                        size="small"
                        siblingCount={0}
                    />
                </Box>
            )}

            {/* Mobile Results Summary */}
            {isMobile && pagination && (
                <Box sx={{ mt: 1, textAlign: 'center' }}>
                    <Typography variant="body2" color="textSecondary">
                        Showing {pagination.from} to {pagination.to} of {pagination.total} branches
                    </Typography>
                </Box>
            )}

            {/* Branch Form Dialog */}
            <BranchForm
                open={openForm}
                branch={selectedBranch}
                mode={formMode}
                onClose={() => {
                    setOpenForm(false);
                    setSelectedBranch(null);
                }}
                onSuccess={(isEdit: boolean, branchName: string) => {
                    setOpenForm(false);
                    setSelectedBranch(null);
                    loadBranches();
                    if (isEdit) {
                        showSuccess(`Branch "${branchName}" has been updated successfully`);
                    } else {
                        showSuccess(`Branch "${branchName}" has been created successfully`);
                    }
                }}
                onError={(message: string) => {
                    showError(message);
                }}
            />

            {/* Branch View Dialog */}
            <BranchView
                open={openView}
                branchId={viewBranchId}
                onClose={() => {
                    setOpenView(false);
                    setViewBranchId(null);
                }}
            />

            {/* Delete Confirmation Dialog */}
            <ConfirmationDialog
                open={deleteDialog}
                title="Delete Branch"
                message={`Are you sure you want to delete "${branchToDelete?.name}"? This action cannot be undone.`}
                onConfirm={confirmDelete}
                onCancel={() => {
                    setDeleteDialog(false);
                    setBranchToDelete(null);
                }}
            />

            {/* Toast Notification */}
            <Toast
                open={toast.open}
                message={toast.message}
                title={toast.title}
                severity={toast.severity}
                onClose={hideToast}
            />
        </Box>
    );
};

export default Branches;
